((window) => {
  function createLocaleCurrencyFormatter() {
    const locale = document.documentElement.lang || undefined;
    const currency = window?.Shopify?.currency?.active || "USD";
    try {
      return new Intl.NumberFormat(locale, {
        style: "currency",
        currency,
      });
    } catch (_e) {
      return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
      });
    }
  }

  const bundleCurrencyFormatter = createLocaleCurrencyFormatter();

  function formatBundleCurrency(value) {
    /* originalUnit/variant prices here come from normalizeShopifyVariantPrice(),
       which reads variant.price from Shopify's own /products/{handle}.js -
       already correctly localized for the active market by Shopify. No
       further rate/currency conversion should be applied here. */
    return bundleCurrencyFormatter.format(Number(value) || 0);
  }

  function getBundleItemVariants(currentProduct) {
    const shopifyVariants = currentProduct?.data?.variants;
    if (Array.isArray(shopifyVariants) && shopifyVariants.length > 0) return shopifyVariants;
    return Array.isArray(currentProduct?.variants) ? currentProduct.variants : [];
  }

  function variantMatchesOptionValue(variant, value, optionIndex) {
    const variantOptions = Array.isArray(variant?.options) ? variant.options : [];
    const option = variantOptions[optionIndex];
    const optionValue =
      (typeof option === "string" ? option : option?.value ?? option?.name) ??
      variant?.[`option${optionIndex + 1}`];
    return String(optionValue ?? "").trim() === String(value ?? "").trim();
  }

  /** Treat missing `available` as in-stock so add-ons / mixed APIs don't grey out everything. */
  function isVariantAvailableForBundleSelection(variant) {
    if (!variant) return false;
    const available = String(variant.available ?? "").toLowerCase();
    if (available === "false" || available === "0") return false;
    const inventoryQuantity = Number(variant.inventory_quantity);
    const inventoryIsTracked = Boolean(variant.inventory_management);
    if (
      inventoryIsTracked &&
      Number.isFinite(inventoryQuantity) &&
      inventoryQuantity <= 0 &&
      variant.inventory_policy !== "continue"
    ) {
      return false;
    }
    return true;
  }

  function parseProductTagsField(tags) {
    if (Array.isArray(tags)) {
      return tags.map((t) => String(t).trim()).filter(Boolean);
    }
    return String(tags || "")
      .split(",")
      .map((t) => t.trim())
      .filter(Boolean);
  }

  function productTagsInclude(tags, needle) {
    const want = String(needle || "").trim();
    if (!want) return true;
    return parseProductTagsField(tags).some(
      (t) => t === want || t.toLowerCase() === want.toLowerCase()
    );
  }

  function getCstmBundleAddOnsConfig(rechargeProductEl) {
    const root = rechargeProductEl.closest("product-info") || document.documentElement;
    const el = root.querySelector("#cstm-bundle-add-ons");
    if (!el) {
      return {
        collectionHandle: null,
        accessoriesTag: "producttype_accessories",
      };
    }
    try {
      const parsed = JSON.parse(el.textContent.trim());
      return {
        collectionHandle: parsed.collectionHandle || null,
        accessoriesTag: parsed.accessoriesTag || "producttype_accessories",
      };
    } catch (_e) {
      return {
        collectionHandle: null,
        accessoriesTag: "producttype_accessories",
      };
    }
  }

  function isBundleProductInStock(productData) {
    if (!productData) return false;
    if (productData.available === false) return false;
    const variants = Array.isArray(productData.variants) ? productData.variants : [];
    if (!variants.length) return true;
    return variants.some((v) => v && v.available !== false);
  }

  async function fetchCollectionAccessoryHandles(collectionHandle, accessoriesTag) {
    const handles = [];
    const seen = new Set();
    let page = 1;
    const limit = 250;
    const maxPages = 20;

    while (page <= maxPages) {
      const url = `/collections/${encodeURIComponent(collectionHandle)}/products.json?limit=${limit}&page=${page}`;
      const res = await fetch(url);
      if (!res.ok) break;
      const data = await res.json();
      const products = data.products || [];
      if (!products.length) break;

      for (const p of products) {
        if (!p?.handle || !productTagsInclude(p.tags, accessoriesTag)) continue;
        if (p.available === false) continue;
        if (!seen.has(p.handle)) {
          seen.add(p.handle);
          handles.push(p.handle);
        }
      }

      if (products.length < limit) break;
      page += 1;
    }

    return handles;
  }

  function normalizeShopifyVariantPrice(variant) {
    if (!variant || variant.price == null) return 0;
    const n = parseFloat(String(variant.price).replace(/[^0-9.-]/g, ""), 10);
    return Number.isFinite(n) ? n : 0;
  }

  function totalBundleSelectedQuantityFromInputs() {
    return Array.from(document.querySelectorAll("input.quantity__input")).reduce((sum, input) => {
      const bundleCardContent = input.closest(".bundle__card-content");
      return sum + (bundleCardContent ? parseInt(input.value, 10) || 0 : 0);
    }, 0);
  }

  function bundleProductExemptFromSubscriptionPriceDisplay(product) {
    if (!product) return false;
    if (product.is_bundle_accessory) return true;
    const pt = String(product?.data?.product_type || "").toLowerCase();
    return pt === "accessories" || pt === "add ons" || pt === "add-ons" || pt === "add on";
  }

  function resolveSelectedVariantForBundleContentItem(item) {
    const product = item.product;
    if (!product?.data?.variants?.length) return null;
    let selected = product.data.variants[0];
    if (product.data.variants.length > 1) {
      const options = [...item.querySelectorAll(".dropdown_variant")].reduce((acc, el, idx) => {
        acc[`option${idx + 1}`] = el.value;
        return acc;
      }, {});
      selected = product.data.variants.find((variant) =>
        Object.keys(options).every((key) => variant[key] === options[key])
      );
    }
    return selected || null;
  }

  function syncBundleCardPriceAttributesInWrapper(wrapper) {
    if (!wrapper) return;
    wrapper.querySelectorAll(".bundle__card").forEach((card) => {
      card.querySelectorAll(".bundle__card-content").forEach((item) => {
        const priceEl = item.querySelector(".bundle__card-price");
        if (!priceEl) return;
        const product = item.product;
        if (!product?.data?.variants) return;
        const sv = resolveSelectedVariantForBundleContentItem(item);
        if (!sv) return;
        const unit = normalizeShopifyVariantPrice(sv);
        priceEl.setAttribute("data-original-price", String(unit));
        const exempt = bundleProductExemptFromSubscriptionPriceDisplay(product);
        priceEl.setAttribute("data-subscription-discount-exempt", exempt ? "true" : "false");
      });
    });
  }

  /**
   * @param {"quantity"|"subscription"|"one-time"} kind
   */
  function formatBundleCardPriceHtml(
    originalUnit,
    totalSelected,
    subscriptionChecked,
    exempt,
    kind
  ) {
    const count = totalSelected < 1 ? 1 : totalSelected;
    const onetimeWidget = Boolean(document.querySelector("recharge-widget #onetime_checkbox"));

    if (exempt) {
      let display = originalUnit;
      let strike = null;
      if (count >= 2 && count <= 5) {
        display = originalUnit * 0.9;
        strike = originalUnit;
      } else if (count >= 6) {
        display = originalUnit * 0.85;
        strike = originalUnit;
      }
      if (strike != null && strike > display + 0.0001) {
        return `<span>${formatBundleCurrency(display)}</span><s>${formatBundleCurrency(strike)}</s>`;
      }
      return `<span>${formatBundleCurrency(display)}</span>`;
    }

    if (kind === "subscription") {
      let display = originalUnit;
      const strike = originalUnit;
      if (count <= 1) {
        display = originalUnit * 0.9;
      } else if (count >= 2 && count <= 5) {
        display = originalUnit * 0.9 * 0.9;
      } else if (count >= 6) {
        display = originalUnit * 0.9 * 0.85;
      }
      return `<span>${formatBundleCurrency(display)}</span><s>${formatBundleCurrency(strike)}</s>`;
    }

    if (kind === "one-time") {
      let display = originalUnit;
      let strike = null;
      if (count === 1) {
        display = originalUnit;
      } else if (count >= 2 && count <= 5) {
        display = originalUnit * 0.9;
        strike = originalUnit;
      } else if (count >= 6) {
        display = originalUnit * 0.85;
        strike = originalUnit;
      }
      if (strike != null) {
        return `<span>${formatBundleCurrency(display)}</span><s>${formatBundleCurrency(strike)}</s>`;
      }
      return `<span>${formatBundleCurrency(display)}</span>`;
    }

    if (subscriptionChecked) {
      if (count <= 1) {
        if (onetimeWidget) {
          return `<span>${formatBundleCurrency(originalUnit * 0.9)}</span><s>${formatBundleCurrency(originalUnit)}</s>`;
        }
        return `<span>${formatBundleCurrency(originalUnit)}</span>`;
      }
      if (count >= 2 && count <= 5) {
        if (onetimeWidget) {
          return `<span>${formatBundleCurrency(originalUnit * 0.9 * 0.9)}</span><s>${formatBundleCurrency(originalUnit)}</s>`;
        }
        return `<span>${formatBundleCurrency(originalUnit * 0.9)}</span><s>${formatBundleCurrency(originalUnit)}</s>`;
      }
      if (count >= 6) {
        if (onetimeWidget) {
          return `<span>${formatBundleCurrency(originalUnit * 0.9 * 0.85)}</span><s>${formatBundleCurrency(originalUnit)}</s>`;
        }
        return `<span>${formatBundleCurrency(originalUnit * 0.85)}</span><s>${formatBundleCurrency(originalUnit)}</s>`;
      }
    } else {
      if (count === 1) {
        return `<span>${formatBundleCurrency(originalUnit)}</span>`;
      }
      if (count >= 2 && count <= 5) {
        return `<span>${formatBundleCurrency(originalUnit * 0.9)}</span><s>${formatBundleCurrency(originalUnit)}</s>`;
      }
      if (count >= 6) {
        return `<span>${formatBundleCurrency(originalUnit * 0.85)}</span><s>${formatBundleCurrency(originalUnit)}</s>`;
      }
    }
    return `<span>${formatBundleCurrency(originalUnit)}</span>`;
  }

  function refreshBundleCardPricesInDocument(kind) {
    const totalSelected = totalBundleSelectedQuantityFromInputs();
    const subscriptionCheckbox = document.querySelector("recharge-widget #subscription_checkbox");
    const subChecked = Boolean(subscriptionCheckbox?.checked);

    document.querySelectorAll(".bundle__card-price").forEach((price) => {
      const raw = price.getAttribute("data-original-price");
      if (raw == null || raw === "") return;
      const originalUnit = parseFloat(raw, 10);
      if (!Number.isFinite(originalUnit)) return;
      const exempt = price.getAttribute("data-subscription-discount-exempt") === "true";
      price.innerHTML = formatBundleCardPriceHtml(
        originalUnit,
        totalSelected,
        subChecked,
        exempt,
        kind
      );
    });
  }

  class RechargeProduct extends HTMLElement {
    constructor() {
      super();
      this.id = this.getAttribute("data-id");
      this.product_handle = this.getAttribute("data-product-handle");
      this.defaults = {
        bundle_template_selector: "#bundle_card_item",
        bundle_dropdown_template_selector: "#bundle_dropdown_template",
        bundle_wrapper_selector: ".bundle__cards-wrapper",
      };
      this.options = Object.assign({}, this.defaults, this.json);
      this.data = null;
      this.purchaseOption = null;
      this.bundle = null;
      this.variants = [];

      this.more_options = [];

      this.cache = {};
      this.default_product_count = 1;
      this.bundleMaxLimit = null;

      document.addEventListener("bundle:item-change", (e) => {
        const totalSelectedItems = Array.from(
          document.querySelectorAll("input.quantity__input")
        ).reduce((sum, input) => {
          const bundleCardContent = input.closest(".bundle__card-content");
          return sum + (bundleCardContent ? parseInt(input.value) || 0 : 0);
        }, 0);

        if (this.bundleMaxLimit !== null) {
          this.querySelectorAll(this.options.bundle_wrapper_selector).forEach((wrapperItem) => {
            const limitMessages = wrapperItem.querySelectorAll("[data-limit-message]");
            limitMessages.forEach((message) => {
              message.style.setProperty(
                "display",
                totalSelectedItems >= this.bundleMaxLimit ? "block" : "none",
                "important"
              );
            });
          });
        }

        this.querySelectorAll(".cstm-bundle-builder-content").forEach((content) => {
          if (content.querySelector(".quantity__input")) {
            const quantityInputs = content.querySelectorAll(".quantity__input");
            const quantity = Array.from(quantityInputs).reduce((acc, input) => {
              return acc + parseInt(input.value);
            }, 0);
            content
              .closest("details")
              .querySelector("[data-product-count]")
              .setAttribute("data-count", quantity);
            content
              .closest("details")
              .querySelector("[data-product-count] .product-count-number").textContent =
              quantity + " ";
          }
        });

        refreshBundleCardPricesInDocument("quantity");
      });

      document.addEventListener("Recharge:Widget:SubscriptionChange", (e) => {
        const changeType = e.detail.type;
        const kind =
          changeType === "subscription"
            ? "subscription"
            : changeType === "one-time"
              ? "one-time"
              : "quantity";
        if (kind === "subscription" || kind === "one-time") {
          setTimeout(() => refreshBundleCardPricesInDocument(kind));
        } else {
          refreshBundleCardPricesInDocument("quantity");
        }
      });
    }

    connectedCallback() {
      if (this.hasAttribute("data-preset-bundle")) {
        document.addEventListener("Recharge:Widget:Loaded", () => {
          this.querySelectorAll("[data-selector-wrapper] .quantity__input").forEach((quantity) => {
            quantity.value = 1;
            quantity.dispatchEvent(new Event("change"));
          });
        });
      }

      this.fetch().then((result) => {
        if (!result.product.in_recharge) {
          document.dispatchEvent(new CustomEvent("Recharge:Widget:Disabled"));
          return false;
        }
        this.data = result;
        this.purchaseOption = result.product.subscription_options.storefront_purchase_options;
        if (result.product.bundle_product) {
          this.bundle = result.product.bundle_product;
          this.bundle.variants = result.product.bundle_product.variants.map((v) => {
            const bundleProductId = Number(this.id);
            const memoryOverrideBundleIds = [9026469560602, 9873295474970];
            const usesMemoryOnlyCollection = memoryOverrideBundleIds.includes(bundleProductId);
            const memoryCollectionId = 472160141594;
            const memoryProductId = 10196349550874;
            v.option_sources = v.option_sources.map((oc) => {
              let collection = window.collections.find(
                (c) => Number(c.id) === Number(oc.collection_id)
              );
              const isMemoryOnlyCollection =
                usesMemoryOnlyCollection && Number(oc.collection_id) === memoryCollectionId;
              if (isMemoryOnlyCollection && !collection) {
                collection = window.collections.find(
                  (c) => c.handle === "all-products-new-collection"
                );
              }
              let products = [];
              if (collection) {
                products = isMemoryOnlyCollection
                  ? collection.products.filter((product) => Number(product.id) === memoryProductId)
                  : collection.products;
              }
              console.log("[RechargeProduct] Resolved bundle option-source collection", {
                bundleProductId: Number(this.id),
                optionSourceCollectionId: Number(oc.collection_id),
                matchedCollection: collection || null,
                includedProducts: products,
              });
              oc.products = products;
              return oc;
            });
            if (usesMemoryOnlyCollection) {
              const memoryOptionSource = v.option_sources.find(
                (optionSource) => Number(optionSource.collection_id) === memoryCollectionId
              );
              const memoryCollection = window.collections.find(
                (collection) =>
                  Number(collection.id) === memoryCollectionId ||
                  collection.handle === "all-products-new-collection"
              );
              const memoryProduct = memoryCollection?.products.find(
                (product) => Number(product.id) === memoryProductId
              ) || {
                id: memoryProductId,
                handle: "memory",
                url: "/products/memory",
                tags: [],
                variants: [],
              };
              if (memoryOptionSource) {
                memoryOptionSource.products = [memoryProduct];
              } else {
                v.option_sources.push({
                  collection_id: memoryCollectionId,
                  products: [memoryProduct],
                });
              }
              console.log("[RechargeProduct] Ensured Memory-only option-source collection", {
                bundleProductId,
                collectionId: memoryCollectionId,
                collectionHandle: "all-products-new-collection",
                includedProducts: [memoryProduct],
              });
            }
            return v;
          });
        }
        this.variants = result.product.variants.map((v) => {
          return {
            id: v.id,
            option_value: v.option_values.find((val) => val).label,
            unit_price: v.prices.unit_price,
            discounted_price: v.prices.discounted_price,

            /**
             * spa = selling allocations
             * pg = plan group
             * sp = selling plan
             */
            selling_plans: v.selling_plan_allocations.map((spa) => {
              let planGroup = result.product.selling_plan_groups.find((pg) => {
                return Number(pg.selling_plan_group_id) === Number(spa.selling_plan_group_id);
              });
              let plan = planGroup.selling_plans.find((sp) => {
                return Number(sp.selling_plan_id) === Number(spa.selling_plan_id);
              });
              return Object.assign({}, spa, { plan });
            }),
          };
        });
        if (result.product.in_recharge) {
          this.render();
        }
      });
    }

    /**
     * Allow multiple variants to be selected, "Add More" feature toggle.
     */
    get allow_multiple_variants() {
      let attribute = this.getAttribute("data-allow-multiple-variants")?.toLowerCase();
      return attribute === "true" || attribute === "1";
    }

    /**
     * Renders the checkbox for each item
     */
    get allow_checkbox_selector() {
      let attribute = this.getAttribute("data-allow-checkbox-selector")?.toLowerCase();
      return attribute === "true" || attribute === "1";
    }

    /**
     * Renders the product based on the purchase option and whether it's a bundle.
     */
    render() {
      // If the product is a bundle, call the buildBundle method regardless of the purchase option
      if (this.bundle) {
        this.buildBundle();
      } else {
        // If the product is not a bundle, call the appropriate method based on the purchase option
        switch (this.purchaseOption) {
          case "subscription_and_onetime":
            this.subscriptionAndOnetime();
            break;
          case "subscription_only":
            this.subscriptionOnly();
            break;
          case "onetime_only":
            this.oneTimeOnly();
            break;
        }
      }
    }

    async resolveBundleProducts() {
      let variants = this.bundle.variants;
      const tasks = [];

      for (const vidx of variants.keys()) {
        for (const ocidx of variants[vidx].option_sources.keys()) {
          for (const pidx of variants[vidx].option_sources[ocidx].products.keys()) {
            const p = variants[vidx].option_sources[ocidx].products[pidx];
            tasks.push({
              vidx,
              ocidx,
              pidx,
              productPromise: this.product(p.url),
            });
          }
        }
      }

      const results = await Promise.all(
        tasks.map((t) => t.productPromise.then((product) => ({ ...t, product })))
      );

      results.forEach(({ vidx, ocidx, pidx, product }) => {
        variants[vidx].option_sources[ocidx].products[pidx].collection_id =
          variants[vidx].option_sources[ocidx].collection_id;
        variants[vidx].option_sources[ocidx].products[pidx].data = product;
      });

      return variants;
    }

    async subscriptionAndOnetime() {
      let selectedVariant = null;

      let radioElements = document.querySelector("variant-radios");
      if (radioElements) {
        let selectedRadio = radioElements.querySelector('input[type="radio"]:checked');
        if (selectedRadio) {
          let variantId = this.getAttribute("data-vid");
          selectedVariant = this.variants.find((v) => Number(v.id) === Number(variantId));
        }
      }

      if (!selectedVariant) {
        selectedVariant = this.variants.find((v) => v);
      }
      this.renderSubscriptionWidget(selectedVariant);

      subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
        let variant = event.data.variant;
        selectedVariant = this.variants.find((v) => Number(v.id) === Number(variant.id));
        this.renderSubscriptionWidget(selectedVariant);
      });
    }

    async oneTimeOnly() {
      document.dispatchEvent(new CustomEvent("Recharge:Widget:Disabled"));
    }

    async subscriptionOnly() {
      let selectedVariant = null;

      let radioElements = document.querySelector("variant-radios");
      if (radioElements) {
        let selectedRadio = radioElements.querySelector('input[type="radio"]:checked');
        if (selectedRadio) {
          let variantId = this.getAttribute("data-vid");
          selectedVariant = this.variants.find((v) => Number(v.id) === Number(variantId));
        }
      }

      if (!selectedVariant) {
        selectedVariant = this.variants.find((v) => v);
      }

      this.renderSubscriptionWidget(selectedVariant);

      subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
        let variant = event.data.variant;
        selectedVariant = this.variants.find((v) => Number(v.id) === Number(variant.id));
        this.renderSubscriptionWidget(selectedVariant);
      });
    }

    async buildBundle() {
      let bundleVariants = await this.resolveBundleProducts();
      let selectedVariant = bundleVariants.find((v) => v);
      let products = selectedVariant.option_sources
        .map((o) => {
          return o.products || [];
        })
        .flat()
        .filter((i) => {
          return Number(this.data.product.external_product_id) !== Number(i.data.id);
        });

      const accessoryProducts = await this.fetchAccessoryProducts();
      if (accessoryProducts.length) {
        const seenProductIds = new Set(products.map((p) => Number(p.id)));
        accessoryProducts.forEach((accessoryProduct) => {
          if (!seenProductIds.has(Number(accessoryProduct.id))) {
            seenProductIds.add(Number(accessoryProduct.id));
            products.push(accessoryProduct);
          }
        });
      }

      products = products.filter((p) => isBundleProductInStock(p?.data));

      let wrapper = this.querySelector(this.options.bundle_wrapper_selector);

      let isBundleBuilder = false;
      let isRechargeBundler = false;
      let cstmBundleBuilder = null;
      let cstmRechargeBundle = null;
      // more than one wrapper due to the build your bundle is a hot pot of different bundles
      if (this.closest("product-info").querySelector("cstmbundle-builder.cstm-mega-bundle")) {
        cstmBundleBuilder = this.closest("product-info").querySelector(
          "cstmbundle-builder.cstm-mega-bundle"
        );
        isBundleBuilder = true;
      }
      if (this.closest("product-info").querySelector("cstmbundle-builder.cstm-recharge-bundle")) {
        cstmRechargeBundle = this.closest("product-info").querySelector(
          "cstmbundle-builder.cstm-recharge-bundle"
        );
        isRechargeBundler = true;
      }
      let templateElement = this.querySelector(this.options.bundle_template_selector);
      let template = templateElement.content.firstElementChild.cloneNode(true);
      let productsPerVendor = [];

      if (isBundleBuilder || isRechargeBundler) {
        products.forEach((currentProduct) => {
          let t = template.cloneNode(true);
          t.querySelector(".bundle__card-media img").setAttribute(
            "src",
            currentProduct.data.image.src
          );
          let contentArray = [];
          for (let i = 0; i < this.default_product_count; i++) {
            let content = t.querySelector(".bundle__card-content").cloneNode(true);

            content.product = currentProduct;
            content.querySelector(".bundle__card-heading").innerHTML = currentProduct.data.title;
            content.querySelector(".bundle__card-description").innerHTML =
              currentProduct.data.custom_metafields?.bundle_item_short_description || "";
            content.querySelector(".quantity__input").setAttribute("data-min", 0);
            content.querySelector(".quantity__input").setAttribute("data-max", 99);
            content.querySelector(".quantity__input").setAttribute("value", 0);
            // For Single Variant Products Only

            const bundlePrice = content.querySelector(".bundle__card-price");
            const v0 = currentProduct.data.variants[0];
            const unit = normalizeShopifyVariantPrice(v0);
            bundlePrice.setAttribute("data-original-price", String(unit));
            bundlePrice.setAttribute(
              "data-subscription-discount-exempt",
              bundleProductExemptFromSubscriptionPriceDisplay(currentProduct) ? "true" : "false"
            );

            const add_more_btn = content.querySelector("[data-add-more]");

            let options = currentProduct.data.options.filter((o) => o.name !== "Title");

            if (options.length) {
              this.more_options.push({
                product_id: currentProduct.id,
                variants_length: currentProduct.data.variants.length,
                cloned_nodes: [],
              });

              const more_options_index = this.more_options.findIndex(
                (option) => option.product_id === currentProduct.id
              );

              if (more_options_index !== -1) {
                this.addToggleEventListener({
                  toggle_element: t.querySelector("[data-toggle-more-fieldsets]"),
                  cloned_nodes: this.more_options[more_options_index].cloned_nodes,
                });
              }

              let dropdownElement = this.querySelector(
                this.options.bundle_dropdown_template_selector
              );

              options.reduce((scopeOptions, currentOption, currentOptionIndex) => {
                let dropdownTemplate = dropdownElement.content.firstElementChild.cloneNode(true);
                dropdownTemplate.querySelector("label").innerHTML = currentOption.name;
                let select = dropdownTemplate.querySelector("select");
                currentOption.values.forEach((v, vi) => {
                  let optionHTML = `<option value="${v}" data-option="${currentOption.name}" data-index="${vi}">${v}</option>`;
                  select.insertAdjacentHTML("beforeend", optionHTML);
                });
                select.setAttribute("data-index", currentOptionIndex);
                select.addEventListener(
                  "change",
                  this.onBundleItemChange.bind(this, bundleVariants)
                );
                if (currentOptionIndex !== 0) {
                  scopeOptions[currentOptionIndex - 1].addEventListener(
                    "change",
                    this.onBundleOptionChange.bind(
                      this,
                      select,
                      currentProduct.data.variants,
                      currentProduct.variants,
                      bundleVariants
                    )
                  );
                }
                if (currentOptionIndex === 0) {
                  const bundleVariantsList = getBundleItemVariants(currentProduct);
                  currentOption.values.forEach((v, vi) => {
                    const optionVariants = bundleVariantsList.filter((variant) =>
                      variantMatchesOptionValue(variant, v, currentOptionIndex)
                    );
                    if (optionVariants.length === 0) {
                      return;
                    }
                    const isOptionVariantsAvailable = optionVariants.some(
                      isVariantAvailableForBundleSelection
                    );
                    const selectOption = Array.from(select.options).find(
                      (option) => String(option.value).trim() === String(v).trim()
                    );
                    if (!isOptionVariantsAvailable && selectOption) {
                      selectOption.disabled = true;
                      selectOption.classList.add("dropdown_variant--soldout");
                      const firstNonDisabledOption = [...select.options].find(
                        (option) => option.disabled === false
                      );
                      if (firstNonDisabledOption) {
                        firstNonDisabledOption.selected = true;
                      }
                    }
                  });
                }
                content
                  .querySelector(".bundle__card-variants-dropdowns")
                  .appendChild(dropdownTemplate);
                return [...scopeOptions, select];
              }, []);

              content.querySelector(".bundle__card-variants-dropdowns").style.display = "";

              add_more_btn.setAttribute("data-add-more", currentProduct.id);
              add_more_btn.addEventListener("click", this.onAddMoreBtnClick.bind(this, content));

              content.classList.toggle("bundle__card-no_variants", !this.allow_multiple_variants);
              add_more_btn.style.display = this.allow_multiple_variants ? "" : "none";
            } else {
              content.classList.add("bundle__card-no_variants");
              content.setAttribute("data-no-variants", true);
              add_more_btn.style.display = "none";
            }
            content
              .querySelector("quantity-input.quantity")
              .addEventListener(
                "QuantityChange",
                this.onBundleItemChange.bind(this, bundleVariants)
              );

            // Handles item duplication within a bundle by triggering a custom event.
            const duplicateHandler = (event) => {
              // Identify the source element for duplication, defaulting to 'content' if unspecified.
              let sourceElement = event.detail.element || content;
              // Create a duplicate of the source element.
              let duplicateElement = sourceElement.cloneNode(true);
              duplicateElement.product = currentProduct; // Associate the product with the duplicated element.

              // Attach the duplicate handler to the new element for potential future duplication.
              duplicateElement.addEventListener("Bundle:Item:Duplicate", duplicateHandler);

              // Attach a 'QuantityChange' event listener to the quantity input of the duplicated element.
              duplicateElement
                .querySelector("quantity-input.quantity")
                .addEventListener(
                  "QuantityChange",
                  this.onBundleItemChange.bind(this, bundleVariants)
                );

              // Iterate over each dropdown variant selector to re-attach necessary event listeners.
              duplicateElement
                .querySelectorAll("select.dropdown_variant")
                .forEach((select, index, selects) => {
                  if (index !== 0) {
                    // For non-first selectors, bind the onBundleOptionChange event listener to the previous selector's change event.
                    selects[index - 1].addEventListener(
                      "change",
                      this.onBundleOptionChange.bind(
                        this,
                        select,
                        currentProduct.data.variants,
                        currentProduct.variants,
                        bundleVariants
                      )
                    );
                  } else {
                    // For the first selector, bind the onBundleItemChange event listener.
                    select.addEventListener(
                      "change",
                      this.onBundleItemChange.bind(this, bundleVariants)
                    );
                  }
                });

              // Attach a click event listener to the 'add more' button of the duplicated element.
              duplicateElement
                .querySelector("[data-add-more]")
                .addEventListener("click", () => this.onAddMoreBtnClick(duplicateElement));

              // If a callback function is provided, execute it with the necessary parameters.
              if (typeof event.detail.callback === "function") {
                event.detail.callback(
                  t,
                  sourceElement,
                  duplicateElement,
                  currentProduct,
                  bundleVariants
                );
              }

              // Append the duplicated element to the bundle card content wrapper in the DOM.
              t.querySelector(".bundle__card-content--wrapper").append(duplicateElement);

              // Manually trigger a 'change' event on all dropdown variant selectors to ensure UI consistency.
              duplicateElement.querySelectorAll("select.dropdown_variant").forEach((select) => {
                let changeEvent = new Event("change", {
                  bubbles: true,
                  cancelable: true,
                });
                select.dispatchEvent(changeEvent);
              });

              // Recompute the bundle configuration to reflect any changes made by duplicating this element.
              this.computeBundleItemChanges(bundleVariants);
            };
            content.addEventListener("Bundle:Item:Duplicate", duplicateHandler);

            contentArray.push(content);
          }

          t.querySelector(".bundle__card-content--wrapper").innerHTML = "";
          contentArray.forEach((contentElement) => {
            t.querySelector(".bundle__card-content--wrapper").insertAdjacentElement(
              "beforeend",
              contentElement
            );
            t.setAttribute("data-product-title", currentProduct.data.title);
            contentElement.querySelectorAll("select.dropdown_variant").forEach((select) => {
              let changeEvent = new Event("change", {
                bubbles: true,
                cancelable: true,
              });
              select.dispatchEvent(changeEvent);
            });
          });

          if (this.allow_checkbox_selector) {
            t.classList.add("with-checkbox");
            t.querySelector(".bundle__card-check").style.display = "";
            t.querySelector("quantity-input.quantity").addEventListener(
              "QuantityChange",
              this.onQuantityBundleItemChange.bind(this, t, bundleVariants)
            );
            t.querySelector('[type="checkbox"]').addEventListener(
              "change",
              this.onSelectBundleItemChange.bind(this, t, bundleVariants)
            );
          }
          try {
            // Assign product-specific custom bundle items to Add-Ons.
            if (
              currentProduct.data.id === 10159969698074 ||
              currentProduct.data.id === 10026342056218 ||
              (Number(this.id) === 9873295474970 &&
                Number(currentProduct.data.id) === 10196349550874)
            ) {
              currentProduct.data.vendor = "accessories";
            }
          } catch (e) {
            console.error("Error setting add on");
          }
          const isBuildYourOwnMemory =
            Number(this.id) === 9873295474970 && Number(currentProduct.data.id) === 10196349550874;
          if (isBuildYourOwnMemory) {
            const addOnsWrapper = this.closest("product-info").querySelector(
              '.product__accordion[data-product-type="accessories"] .bundle__cards-wrapper, ' +
                '.cstm-bundle-builder-content[data-vendor="accessories"] .bundle__cards-wrapper'
            );
            if (addOnsWrapper) {
              addOnsWrapper.insertAdjacentElement("beforeend", t);
              const addOnsCount = productsPerVendor.find(
                (product) => product.vendor === "accessories"
              );
              if (addOnsCount) {
                addOnsCount.products += 1;
              } else {
                productsPerVendor.push({ vendor: "accessories", products: 1 });
              }
            } else {
              console.warn("[RechargeProduct] Existing Add-Ons accordion was not found", {
                bundleProductId: Number(this.id),
                productId: Number(currentProduct.data.id),
              });
            }
            return;
          }
          if (cstmBundleBuilder) {
            if (
              cstmBundleBuilder.querySelector(
                `.cstm-bundle-builder-content[data-vendor="${currentProduct.data.vendor}"]`
              )
            ) {
              cstmBundleBuilder
                .querySelector(
                  `.cstm-bundle-builder-content[data-vendor="${currentProduct.data.vendor}"] .bundle__cards-wrapper`
                )
                .insertAdjacentElement("beforeend", t);
            }

            if (
              productsPerVendor.find((product) => product.vendor === currentProduct.data.vendor)
            ) {
              productsPerVendor.find(
                (product) => product.vendor === currentProduct.data.vendor
              ).products += 1;
            } else {
              productsPerVendor.push({
                vendor: currentProduct.data.vendor,
                products: 1,
              });
            }
          } else if (cstmRechargeBundle) {
            let placedRechargeBundle = false;
            const typesScript = cstmRechargeBundle
              .closest("product-info")
              .querySelector("#cstm-bundle-types");
            if (typesScript) {
              const bundleTypes = JSON.parse(typesScript.textContent.trim());
              const productTags = Array.isArray(currentProduct.tags) ? currentProduct.tags : [];
              // "Build Your Own Bundle" and the Bar Soap & Shampoo Bar Bundle group shampoo bar /
              // conditioner bar tags under a combined "hair care" accordion (see
              // cstm-bundle-builder.liquid / cstm-bundle-card.liquid is_mega_bundle mapping and the
              // producttype_shampoo bar override for product 9026469560602).
              const isMemorySoapOverride =
                Number(bundleTypes.productId) === 9026469560602 &&
                Number(currentProduct.data.id) === 10196349550874;
              const hairCareAliases = ["shampoo bar", "conditioner bar"];
              bundleTypes.types.forEach((type) => {
                if (
                  cstmRechargeBundle.querySelector(
                    `.product__accordion[data-product-type="${type}"]`
                  ) &&
                  (isMemorySoapOverride
                    ? type.toLowerCase() === "soap"
                    : productTags.find((tag) => {
                        const rawType = tag.replace("producttype_", "");
                        return (
                          rawType === type ||
                          (type === "hair care" && hairCareAliases.includes(rawType.toLowerCase()))
                        );
                      }))
                ) {
                  cstmRechargeBundle
                    .querySelector(
                      `.product__accordion[data-product-type="${type}"] .bundle__cards-wrapper`
                    )
                    .insertAdjacentElement("beforeend", t);
                  placedRechargeBundle = true;
                  if (productsPerVendor.find((product) => product.vendor === type)) {
                    productsPerVendor.find((product) => product.vendor === type).products += 1;
                  } else {
                    productsPerVendor.push({
                      vendor: type,
                      products: 1,
                    });
                  }
                }
              });
            }
            if (!placedRechargeBundle && currentProduct.is_bundle_accessory) {
              const accessoriesWrap = cstmRechargeBundle.querySelector(
                '.product__accordion[data-product-type="accessories"] .bundle__cards-wrapper'
              );
              if (accessoriesWrap) {
                accessoriesWrap.insertAdjacentElement("beforeend", t);
                const accKey = "accessories";
                if (productsPerVendor.find((product) => product.vendor === accKey)) {
                  productsPerVendor.find((product) => product.vendor === accKey).products += 1;
                } else {
                  productsPerVendor.push({
                    vendor: accKey,
                    products: 1,
                  });
                }
              }
            }
          }
        });

        try {
          productsPerVendor.forEach((product) => {
            cstmBundleBuilder
              .querySelector(`.cstm-bundle-builder-content[data-vendor="${product.vendor}"]`)
              .setAttribute("data-product-count", product.products);
          });

          const quantityInputs = cstmBundleBuilder.querySelectorAll(".quantity__input");
          quantityInputs.forEach((target) => {
            target.addEventListener("change", () => {
              document.dispatchEvent(new CustomEvent("Bundle:Item:Select"));
            });
          });
        } catch (e) {
          console.info("Error setting product count", e);
        }

        document.dispatchEvent(new CustomEvent("Bundle:Builder:StartLoad"));
      } else {
        products.forEach((currentProduct) => {
          let t = template.cloneNode(true);
          t.querySelector(".bundle__card-media img").setAttribute(
            "src",
            currentProduct.data.image.src
          );

          let contentArray = [];
          for (let i = 0; i < this.default_product_count; i++) {
            let content = t.querySelector(".bundle__card-content").cloneNode(true);

            content.product = currentProduct;
            content.querySelector(".bundle__card-heading").innerHTML = currentProduct.data.title;
            content.querySelector(".bundle__card-description").innerHTML =
              currentProduct.data.custom_metafields?.bundle_item_short_description || "";
            content.querySelector(".quantity__input").setAttribute("data-min", 0);
            content.querySelector(".quantity__input").setAttribute("data-max", 99);
            content.querySelector(".quantity__input").setAttribute("value", 0);

            const bundlePrice = content.querySelector(".bundle__card-price");
            const v0b = currentProduct.data.variants[0];
            const unitB = normalizeShopifyVariantPrice(v0b);
            bundlePrice.setAttribute("data-original-price", String(unitB));
            bundlePrice.setAttribute(
              "data-subscription-discount-exempt",
              bundleProductExemptFromSubscriptionPriceDisplay(currentProduct) ? "true" : "false"
            );

            const add_more_btn = content.querySelector("[data-add-more]");

            let options = currentProduct.data.options.filter((o) => o.name !== "Title");

            if (options.length) {
              this.more_options.push({
                product_id: currentProduct.id,
                variants_length: currentProduct.data.variants.length,
                cloned_nodes: [],
              });

              const more_options_index = this.more_options.findIndex(
                (option) => option.product_id === currentProduct.id
              );

              if (more_options_index !== -1) {
                this.addToggleEventListener({
                  toggle_element: t.querySelector("[data-toggle-more-fieldsets]"),
                  cloned_nodes: this.more_options[more_options_index].cloned_nodes,
                });
              }

              let dropdownElement = this.querySelector(
                this.options.bundle_dropdown_template_selector
              );

              options.reduce((scopeOptions, currentOption, currentOptionIndex) => {
                let dropdownTemplate = dropdownElement.content.firstElementChild.cloneNode(true);
                dropdownTemplate.querySelector("label").innerHTML = currentOption.name;
                let select = dropdownTemplate.querySelector("select");
                currentOption.values.forEach((v, vi) => {
                  let optionHTML = `<option value="${v}" data-option="${currentOption.name}" data-index="${vi}">${v}</option>`;
                  select.insertAdjacentHTML("beforeend", optionHTML);
                });
                select.setAttribute("data-index", currentOptionIndex);
                select.addEventListener(
                  "change",
                  this.onBundleItemChange.bind(this, bundleVariants)
                );
                if (currentOptionIndex !== 0) {
                  scopeOptions[currentOptionIndex - 1].addEventListener(
                    "change",
                    this.onBundleOptionChange.bind(
                      this,
                      select,
                      currentProduct.data.variants,
                      currentProduct.variants,
                      bundleVariants
                    )
                  );
                }
                if (currentOptionIndex === 0) {
                  const bundleVariantsList = getBundleItemVariants(currentProduct);
                  currentOption.values.forEach((v, vi) => {
                    const optionVariants = bundleVariantsList.filter((variant) =>
                      variantMatchesOptionValue(variant, v, currentOptionIndex)
                    );
                    if (optionVariants.length === 0) {
                      return;
                    }
                    const isOptionVariantsAvailable = optionVariants.some(
                      isVariantAvailableForBundleSelection
                    );
                    const selectOption = Array.from(select.options).find(
                      (option) => String(option.value).trim() === String(v).trim()
                    );
                    if (!isOptionVariantsAvailable && selectOption) {
                      selectOption.disabled = true;
                      selectOption.classList.add("dropdown_variant--soldout");
                      const firstNonDisabledOption = [...select.options].find(
                        (option) => option.disabled === false
                      );
                      if (firstNonDisabledOption) {
                        firstNonDisabledOption.selected = true;
                      }
                    }
                  });
                }
                content
                  .querySelector(".bundle__card-variants-dropdowns")
                  .appendChild(dropdownTemplate);
                return [...scopeOptions, select];
              }, []);

              content.querySelector(".bundle__card-variants-dropdowns").style.display = "";

              add_more_btn.setAttribute("data-add-more", currentProduct.id);
              add_more_btn.addEventListener("click", this.onAddMoreBtnClick.bind(this, content));

              content.classList.toggle("bundle__card-no_variants", !this.allow_multiple_variants);
              add_more_btn.style.display = this.allow_multiple_variants ? "" : "none";
            } else {
              content.classList.add("bundle__card-no_variants");
              content.setAttribute("data-no-variants", true);
              add_more_btn.style.display = "none";
            }
            content
              .querySelector("quantity-input.quantity")
              .addEventListener(
                "QuantityChange",
                this.onBundleItemChange.bind(this, bundleVariants)
              );

            // Handles item duplication within a bundle by triggering a custom event.
            const duplicateHandler = (event) => {
              // Identify the source element for duplication, defaulting to 'content' if unspecified.
              let sourceElement = event.detail.element || content;
              // Create a duplicate of the source element.
              let duplicateElement = sourceElement.cloneNode(true);
              duplicateElement.product = currentProduct; // Associate the product with the duplicated element.

              // Attach the duplicate handler to the new element for potential future duplication.
              duplicateElement.addEventListener("Bundle:Item:Duplicate", duplicateHandler);

              // Attach a 'QuantityChange' event listener to the quantity input of the duplicated element.
              duplicateElement
                .querySelector("quantity-input.quantity")
                .addEventListener(
                  "QuantityChange",
                  this.onBundleItemChange.bind(this, bundleVariants)
                );

              // Iterate over each dropdown variant selector to re-attach necessary event listeners.
              duplicateElement
                .querySelectorAll("select.dropdown_variant")
                .forEach((select, index, selects) => {
                  if (index !== 0) {
                    // For non-first selectors, bind the onBundleOptionChange event listener to the previous selector's change event.
                    selects[index - 1].addEventListener(
                      "change",
                      this.onBundleOptionChange.bind(
                        this,
                        select,
                        currentProduct.data.variants,
                        currentProduct.variants,
                        bundleVariants
                      )
                    );
                  } else {
                    // For the first selector, bind the onBundleItemChange event listener.
                    select.addEventListener(
                      "change",
                      this.onBundleItemChange.bind(this, bundleVariants)
                    );
                  }
                });

              // Attach a click event listener to the 'add more' button of the duplicated element.
              duplicateElement
                .querySelector("[data-add-more]")
                .addEventListener("click", () => this.onAddMoreBtnClick(duplicateElement));

              // If a callback function is provided, execute it with the necessary parameters.
              if (typeof event.detail.callback === "function") {
                event.detail.callback(
                  t,
                  sourceElement,
                  duplicateElement,
                  currentProduct,
                  bundleVariants
                );
              }

              // Append the duplicated element to the bundle card content wrapper in the DOM.
              t.querySelector(".bundle__card-content--wrapper").append(duplicateElement);

              // Manually trigger a 'change' event on all dropdown variant selectors to ensure UI consistency.
              duplicateElement.querySelectorAll("select.dropdown_variant").forEach((select) => {
                let changeEvent = new Event("change", {
                  bubbles: true,
                  cancelable: true,
                });
                select.dispatchEvent(changeEvent);
              });

              // Recompute the bundle configuration to reflect any changes made by duplicating this element.
              this.computeBundleItemChanges(bundleVariants);
            };
            content.addEventListener("Bundle:Item:Duplicate", duplicateHandler);

            contentArray.push(content);
          }

          t.querySelector(".bundle__card-content--wrapper").innerHTML = "";
          contentArray.forEach((contentElement) => {
            t.querySelector(".bundle__card-content--wrapper").insertAdjacentElement(
              "beforeend",
              contentElement
            );
            t.setAttribute("data-product-title", currentProduct.data.title);
            contentElement.querySelectorAll("select.dropdown_variant").forEach((select) => {
              let changeEvent = new Event("change", {
                bubbles: true,
                cancelable: true,
              });
              select.dispatchEvent(changeEvent);
            });
          });

          if (this.allow_checkbox_selector) {
            t.classList.add("with-checkbox");
            t.querySelector(".bundle__card-check").style.display = "";
            t.querySelector("quantity-input.quantity").addEventListener(
              "QuantityChange",
              this.onQuantityBundleItemChange.bind(this, t, bundleVariants)
            );
            t.querySelector('[type="checkbox"]').addEventListener(
              "change",
              this.onSelectBundleItemChange.bind(this, t, bundleVariants)
            );
          }
          wrapper.insertAdjacentElement("beforeend", t);
        });
      }
      this.renderSubscriptionWidget(selectedVariant);
      this.computeBundleItemChanges(bundleVariants);
      document.dispatchEvent(new CustomEvent("Recharge:Widget:Loaded"));

      // Preselect product(s) if specified in URL parameters
      // Wait a bit for DOM to be fully rendered
      setTimeout(() => {
        this.preselectProductFromURL();
        this.preselectBundleItemsFromURL();
      }, 300);
    }

    /**
     * Preselects multiple bundle items when returning from the cart drawer's "Edit Bundle"
     * link, e.g. /products/build-your-own-bundle?edit_bundle_items=handle1:variant1:qty1,handle2:variant2:qty2
     * The bundle cards are populated asynchronously (Storefront API fetch), so this retries
     * until they exist in the DOM.
     * Items are applied sequentially so the same product with multiple variants can
     * create additional "Add More" rows instead of overwriting the first row.
     */
    async preselectBundleItemsFromURL() {
      const urlParams = new URLSearchParams(window.location.search);
      const editBundleItems = urlParams.get("edit_bundle_items");

      if (!editBundleItems) {
        return;
      }

      const items = editBundleItems
        .split(",")
        .map((entry) => {
          const [handle, variantId, quantity] = entry.split(":");
          return { handle, variantId, quantity: parseInt(quantity, 10) || 1 };
        })
        .filter((item) => item.handle);

      if (items.length === 0) {
        return;
      }

      const bundleCards = this.querySelectorAll(".bundle__card");

      if (bundleCards.length === 0) {
        setTimeout(() => {
          this.preselectBundleItemsFromURL();
        }, 500);
        return;
      }

      for (const item of items) {
        await this.preselectBundleCardByHandle(item.handle, item.variantId, item.quantity);
      }
    }

    /**
     * Clones an additional option/qty row on a bundle card (same path as Add More).
     * Resolves once a new `.bundle__card-content` is present.
     */
    ensureExtraBundleRow(card) {
      const contentsBefore = card.querySelectorAll(".bundle__card-content").length;
      let source = null;

      for (const content of card.querySelectorAll(".bundle__card-content")) {
        const btn = content.querySelector("[data-add-more]");
        if (btn && getComputedStyle(btn).display !== "none") {
          source = content;
          break;
        }
      }

      if (!source) {
        source = card.querySelector(".bundle__card-content");
      }

      if (!source) {
        return Promise.resolve(null);
      }

      this.onAddMoreBtnClick(source);

      return new Promise((resolve) => {
        const start = Date.now();
        const tick = () => {
          const contents = card.querySelectorAll(".bundle__card-content");
          if (contents.length > contentsBefore) {
            // Wait for selectNextUnselectedVariant's post-clone setTimeout(0)
            setTimeout(() => resolve(contents[contents.length - 1]), 50);
            return;
          }
          if (Date.now() - start > 2000) {
            resolve(null);
            return;
          }
          requestAnimationFrame(tick);
        };
        tick();
      });
    }

    /**
     * Finds the bundle card for a given product handle and sets its quantity (and variant,
     * when provided) to preselect it. When the card already has a selected row and the
     * product supports multiple variants, creates another row via Add More first.
     */
    async preselectBundleCardByHandle(handle, variantId, quantity) {
      const bundleCards = this.querySelectorAll(".bundle__card");
      let matchingCard = null;

      for (const card of bundleCards) {
        const cardContent = card.querySelector(".bundle__card-content");
        if (!cardContent || !cardContent.product) {
          continue;
        }

        const productData = cardContent.product.data;
        const productObj = cardContent.product;

        let productHandle = productData.handle || null;
        if (!productHandle && productObj.url) {
          const urlMatch = productObj.url.match(/\/products\/([^/?]+)/);
          if (urlMatch) productHandle = urlMatch[1];
        }
        if (!productHandle && productData.url) {
          const urlMatch = productData.url.match(/\/products\/([^/?]+)/);
          if (urlMatch) productHandle = urlMatch[1];
        }

        if (productHandle === handle) {
          matchingCard = card;
          break;
        }
      }

      if (!matchingCard) {
        return;
      }

      const firstContent = matchingCard.querySelector(".bundle__card-content");
      if (!firstContent) {
        return;
      }

      const soldOutBadge = matchingCard.querySelector(".badge_soldout");
      const isProductAvailable =
        firstContent.product &&
        firstContent.product.data &&
        firstContent.product.data.available !== false;
      const isBadgeVisible =
        soldOutBadge &&
        soldOutBadge.offsetParent !== null &&
        window.getComputedStyle(soldOutBadge).display !== "none" &&
        window.getComputedStyle(soldOutBadge).visibility !== "hidden";

      if (isBadgeVisible && !isProductAvailable) {
        return;
      }

      const detailsElement = matchingCard.closest("details");
      if (detailsElement && !detailsElement.hasAttribute("open")) {
        detailsElement.setAttribute("open", "");
      }

      let targetContent = [...matchingCard.querySelectorAll(".bundle__card-content")].find(
        (content) => Number(content.querySelector(".quantity__input")?.value || 0) === 0
      );

      if (!targetContent) {
        const productData = firstContent.product?.data;
        const hasSelectableOptions = productData?.options?.some((o) => o.name !== "Title");
        const canAddMore =
          this.allow_multiple_variants &&
          hasSelectableOptions &&
          productData?.variants?.length > 1;

        if (canAddMore) {
          targetContent = await this.ensureExtraBundleRow(matchingCard);
        }

        if (!targetContent) {
          targetContent = firstContent;
        }
      }

      const quantityInput = targetContent.querySelector(".quantity__input");
      if (!quantityInput) {
        return;
      }

      if (variantId && targetContent.product) {
        const productData = targetContent.product.data;
        const matchingVariant = productData.variants.find(
          (v) => String(v.id) === String(variantId)
        );

        if (matchingVariant && productData.variants.length > 1) {
          const variantSelects = targetContent.querySelectorAll(".dropdown_variant");
          if (variantSelects.length > 0) {
            const optionValues = [];
            if (matchingVariant.option1) optionValues.push(matchingVariant.option1);
            if (matchingVariant.option2) optionValues.push(matchingVariant.option2);
            if (matchingVariant.option3) optionValues.push(matchingVariant.option3);

            variantSelects.forEach((select, index) => {
              if (optionValues[index]) {
                const optionValue = optionValues[index];
                const option = Array.from(select.options).find(
                  (opt) => opt.value === optionValue || opt.textContent.trim() === optionValue
                );
                if (option && !option.disabled) {
                  select.value = option.value;
                  select.dispatchEvent(new Event("change", { bubbles: true, cancelable: true }));
                }
              }
            });
          }
        }
      }

      quantityInput.setAttribute("value", quantity);
      quantityInput.value = quantity;

      const minusButton = targetContent.querySelector('button[name="minus"]');
      if (minusButton) {
        minusButton.classList.remove("disabled");
      }

      await new Promise((resolve) => {
        setTimeout(() => {
          quantityInput.dispatchEvent(new Event("change", { bubbles: true, cancelable: true }));

          const quantityInputElement = targetContent.querySelector("quantity-input.quantity");
          if (quantityInputElement) {
            quantityInputElement.dispatchEvent(
              new CustomEvent("QuantityChange", { bubbles: true, cancelable: true })
            );
          }

          document.dispatchEvent(new CustomEvent("bundle:item-change"));
          resolve();
        }, 100);
      });
    }

    preselectProductFromURL() {
      const urlParams = new URLSearchParams(window.location.search);
      const preselectHandle = urlParams.get("preselect_handle");
      const preselectVariant = urlParams.get("preselect_variant");

      if (!preselectHandle) {
        return;
      }

      const bundleCards = this.querySelectorAll(".bundle__card");

      if (bundleCards.length === 0) {
        setTimeout(() => {
          this.preselectProductFromURL();
        }, 500);
        return;
      }

      let matchingCard = null;

      for (const card of bundleCards) {
        const productTitle = card.getAttribute("data-product-title");

        if (!productTitle) {
          continue;
        }

        const cardContent = card.querySelector(".bundle__card-content");
        if (!cardContent) {
          continue;
        }

        if (!cardContent.product) {
          continue;
        }

        const productData = cardContent.product.data;
        const productObj = cardContent.product;

        let productHandle = null;

        if (productData.handle) {
          productHandle = productData.handle;
        } else if (productObj.url) {
          const urlMatch = productObj.url.match(/\/products\/([^/?]+)/);
          if (urlMatch) {
            productHandle = urlMatch[1];
          }
        } else if (productData.url) {
          const urlMatch = productData.url.match(/\/products\/([^/?]+)/);
          if (urlMatch) {
            productHandle = urlMatch[1];
          }
        }

        if (productHandle && productHandle === preselectHandle) {
          matchingCard = card;
          break;
        }

        const normalizedTitle = productTitle.toLowerCase().trim();
        const normalizedHandle = preselectHandle.toLowerCase().replace(/-/g, " ").trim();

        const handleWords = normalizedHandle.split(/\s+/);
        const titleWords = normalizedTitle.split(/\s+/);
        const handlePrefix = handleWords.slice(0, titleWords.length).join(" ");
        const titleStr = titleWords.join(" ");

        let matches = false;
        if (normalizedTitle === normalizedHandle) {
          matches = true;
        } else if (
          normalizedHandle.includes(normalizedTitle) ||
          normalizedTitle.includes(normalizedHandle)
        ) {
          matches = true;
        } else if (
          titleStr === handlePrefix ||
          handlePrefix.startsWith(titleStr) ||
          titleStr.startsWith(handlePrefix)
        ) {
          matches = true;
        } else if (
          titleWords.length > 0 &&
          titleWords.every((word, idx) => handleWords[idx] === word)
        ) {
          matches = true;
        }

        if (matches) {
          matchingCard = card;
          break;
        }
      }

      if (!matchingCard) {
        return; // Product not found in bundle
      }

      const firstContent = matchingCard.querySelector(".bundle__card-content");

      const soldOutBadge = matchingCard.querySelector(".badge_soldout");
      const isProductAvailable =
        firstContent &&
        firstContent.product &&
        firstContent.product.data &&
        firstContent.product.data.available !== false;

      const isBadgeVisible =
        soldOutBadge &&
        soldOutBadge.offsetParent !== null &&
        window.getComputedStyle(soldOutBadge).display !== "none" &&
        window.getComputedStyle(soldOutBadge).visibility !== "hidden";

      if (isBadgeVisible && !isProductAvailable) {
        return;
      }
      if (!firstContent) {
        return;
      }

      const quantityInput = firstContent.querySelector(".quantity__input");
      if (!quantityInput) {
        return;
      }

      const detailsElement = matchingCard.closest("details");
      if (detailsElement && !detailsElement.hasAttribute("open")) {
        detailsElement.setAttribute("open", "");
      }

      quantityInput.setAttribute("value", "1");
      quantityInput.value = "1";

      const minusButton = firstContent.querySelector('button[name="minus"]');
      if (minusButton) {
        minusButton.classList.remove("disabled");
      }

      if (preselectVariant && firstContent.product) {
        const productData = firstContent.product.data;
        const matchingVariant = productData.variants.find(
          (v) => String(v.id) === String(preselectVariant)
        );

        if (matchingVariant && productData.variants.length > 1) {
          const variantSelects = firstContent.querySelectorAll(".dropdown_variant");
          if (variantSelects.length > 0) {
            const optionValues = [];
            if (matchingVariant.option1) optionValues.push(matchingVariant.option1);
            if (matchingVariant.option2) optionValues.push(matchingVariant.option2);
            if (matchingVariant.option3) optionValues.push(matchingVariant.option3);

            if (optionValues.length === 0 && matchingVariant.options) {
              matchingVariant.options.forEach((opt) => {
                const value = opt.name || opt.label || opt;
                if (value) optionValues.push(value);
              });
            }

            variantSelects.forEach((select, index) => {
              if (optionValues[index]) {
                const optionValue = optionValues[index];
                const option = Array.from(select.options).find(
                  (opt) => opt.value === optionValue || opt.textContent.trim() === optionValue
                );
                if (option && !option.disabled) {
                  select.value = option.value;
                  const changeEvent = new Event("change", {
                    bubbles: true,
                    cancelable: true,
                  });
                  select.dispatchEvent(changeEvent);
                }
              }
            });
          }
        }
      }

      setTimeout(() => {
        const changeEvent = new Event("change", {
          bubbles: true,
          cancelable: true,
        });
        quantityInput.dispatchEvent(changeEvent);

        const quantityInputElement = firstContent.querySelector("quantity-input.quantity");
        if (quantityInputElement) {
          const quantityChangeEvent = new CustomEvent("QuantityChange", {
            bubbles: true,
            cancelable: true,
          });
          quantityInputElement.dispatchEvent(quantityChangeEvent);
        }

        document.dispatchEvent(new CustomEvent("bundle:item-change"));
      }, 100);

      setTimeout(() => {
        matchingCard.scrollIntoView({ behavior: "smooth", block: "nearest" });
      }, 100);
    }

    isElementInViewport(el) {
      const rect = el.getBoundingClientRect();
      return (
        rect.top >= 0 &&
        rect.left >= 0 &&
        rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
        rect.right <= (window.innerWidth || document.documentElement.clientWidth)
      );
    }

    /**
     * Adds an event listener to the toggle element to handle showing and hiding more fieldsets.
     * @param {Object} params - The parameters.
     * @param {HTMLElement} params.toggle_element - The toggle button element.
     * @param {Array} params.cloned_nodes - The array of cloned fieldsets.
     */
    addToggleEventListener({ toggle_element, cloned_nodes }) {
      toggle_element.addEventListener("click", () => {
        const parent_element = toggle_element.parentElement;
        parent_element.classList.toggle("collapsed");

        if (parent_element.classList.contains("collapsed")) {
          toggle_element.textContent =
            "Show more (" + cloned_nodes.length + " more products selected)";
          if (!this.isElementInViewport(parent_element)) {
            parent_element.scrollIntoView({
              behavior: "smooth",
              block: "start",
            });
          }
        } else {
          toggle_element.textContent = "Show Less";
        }
      });
    }

    /**
     * Defaults a newly duplicated row's dropdowns to the next variant combo
     * not already selected on the card. Leaves cloned values if every combo is used.
     */
    selectNextUnselectedVariant(parentBundleCard, newElement, product) {
      if (!parentBundleCard || !newElement || !product?.data?.variants?.length) return;

      const dropdowns = [...newElement.querySelectorAll("select.dropdown_variant")];
      if (!dropdowns.length) return;

      const usedKeys = new Set();
      parentBundleCard.querySelectorAll(".bundle__card-content").forEach((item) => {
        if (item === newElement) return;
        const opts = [...item.querySelectorAll("select.dropdown_variant")].reduce(
          (acc, el, idx) => {
            acc[`option${idx + 1}`] = el.value;
            return acc;
          },
          {}
        );
        usedKeys.add(JSON.stringify(opts));
      });

      const nextVariant = product.data.variants.find((variant) => {
        const opts = {};
        dropdowns.forEach((_select, idx) => {
          opts[`option${idx + 1}`] = variant[`option${idx + 1}`];
        });
        return !usedKeys.has(JSON.stringify(opts));
      });

      if (!nextVariant) return;

      dropdowns.forEach((select, idx) => {
        const value = nextVariant[`option${idx + 1}`];
        if (value == null) return;
        const matchingOption = [...select.options].find((option) => option.value === value);
        if (matchingOption && !matchingOption.disabled) {
          select.value = value;
        }
      });
    }

    /**
     * Handles the click event for adding more fieldsets.
     */
    onAddMoreBtnClick(content, _event) {
      // Callback function for post-duplication adjustments
      const afterDuplicationCallback = (
        template,
        fromElement,
        newElement,
        product,
        bundleVariants
      ) => {
        const parentBundleCard = fromElement.closest(".bundle__card");

        if (parentBundleCard) {
          parentBundleCard.classList.remove("collapsed");

          const toggleCollapseBtn = parentBundleCard.querySelector("[data-toggle-more-fieldsets]");

          if (toggleCollapseBtn) {
            toggleCollapseBtn.textContent = "Show Less";
          }
        }
        // Hide the 'add more' button in the source element
        fromElement.querySelector("[data-add-more]").style.display = "none";

        // Hide header and description in the duplicated element
        newElement.querySelector(".bundle__card-header").style.display = "none";
        newElement.querySelector(".bundle__card-description").style.display = "none";

        const quantityInput = newElement.querySelector(".quantity__input");

        quantityInput.setAttribute("value", 0);
        quantityInput.value = 0;

        this.selectNextUnselectedVariant(parentBundleCard, newElement, product);
        // Dependent option selects may reset during post-clone change events; re-apply after.
        setTimeout(() => {
          this.selectNextUnselectedVariant(parentBundleCard, newElement, product);
          this.computeBundleItemChanges(bundleVariants);
        }, 0);

        // Find the index of the product in the more_options array
        const moreOptionsIndex = this.more_options.findIndex(
          (option) => option.product_id === product.id
        );
        if (moreOptionsIndex !== -1) {
          const moreOptions = this.more_options[moreOptionsIndex];
          moreOptions.cloned_nodes.push(newElement);

          // If the limit of variants is reached, hide the 'add more' button and show the limit message
          if (moreOptions.cloned_nodes.length + 1 >= moreOptions.variants_length) {
            newElement.querySelector("[data-add-more]").style.display = "none";
            newElement.querySelector("[data-limit-message]").style.display = "block";
          }

          // Update visibility of fieldsets based on the cloned nodes array
          this.toggleMoreFieldsetsVisibility(template, moreOptions.cloned_nodes);

          // Ensure there is a delete button, and add event listener
          let deleteButton = newElement.querySelector("[data-delete]");

          if (!deleteButton) {
            deleteButton = document.createElement("button");
            deleteButton.setAttribute("type", "button");
            deleteButton.setAttribute("data-delete", "");
            deleteButton.setAttribute("aria-label", "Delete");
            deleteButton.setAttribute("class", "bundle__card-btn bundle__card-delete-btn");
            newElement.querySelector("[data-selector-wrapper]").appendChild(deleteButton);
          }
          this.addDeleteEventListener(
            deleteButton,
            newElement,
            product.id,
            fromElement,
            template,
            bundleVariants
          );
        }
      };

      // Dispatch custom event to trigger item duplication with the callback
      content.dispatchEvent(
        new CustomEvent("Bundle:Item:Duplicate", {
          detail: { element: content, callback: afterDuplicationCallback },
        })
      );
    }

    /**
     * Adds an event listener to the delete button to manage the deletion of a fieldset.
     * @param {HTMLElement} deleteButton - The delete button element.
     * @param {HTMLElement} element - The cloned element to be deleted.
     * @param {number} productId - The ID of the product associated with the element.
     * @param {HTMLElement} prevElement - The element preceding the one to be deleted.
     * @param {HTMLElement} template - The template element used for creating new elements.
     * @param {HTMLElement} bundleVariants - The array of bundle variants from Recharge.
     */
    addDeleteEventListener(
      deleteButton,
      element,
      productId,
      prevElement,
      template,
      bundleVariants
    ) {
      deleteButton.addEventListener("click", () => {
        const parentBundleCard = deleteButton.closest(".bundle__card-content");

        parentBundleCard.classList.add("removing");

        setTimeout(() => {
          // Remove the cloned fieldset from the DOM
          element.remove();

          // Find the corresponding entry in the more_options array
          const moreOptionsIndex = this.more_options.findIndex(
            (option) => option.product_id === productId
          );
          if (moreOptionsIndex === -1) return;

          // Remove the element from the cloned_nodes array
          const clonedNodes = this.more_options[moreOptionsIndex].cloned_nodes;
          const clonedNodeIndex = clonedNodes.indexOf(element);
          if (clonedNodeIndex !== -1) {
            clonedNodes.splice(clonedNodeIndex, 1);
          }

          // Determine visibility of the 'add more' button
          const shouldShowAddMore =
            clonedNodes.length + 1 < this.more_options[moreOptionsIndex].variants_length;
          const displayStyle = shouldShowAddMore ? "flex" : "none";

          // Update the visibility for the last element or the template if no cloned nodes remain
          const targetElement =
            clonedNodes.length === 0
              ? template.querySelector(".bundle__card-content")
              : clonedNodes[clonedNodes.length - 1];
          targetElement.querySelector("[data-add-more]").style.display = displayStyle;
          targetElement.querySelector("[data-limit-message]").style.display = shouldShowAddMore
            ? "none"
            : "block";

          // Update the visibility of additional fieldsets
          this.toggleMoreFieldsetsVisibility(template, clonedNodes);

          // Recalculate the changes to the bundle configuration
          this.computeBundleItemChanges(bundleVariants);
        }, 350);
      });
    }

    /**
     * Toggles the visibility of the toggle element based on the cloned nodes array.
     * @param {HTMLElement} template - The template element.
     * @param {Array} cloned_nodes - The array of cloned fieldsets.
     */
    toggleMoreFieldsetsVisibility(template, cloned_nodes) {
      const toggle_element = template.querySelector("[data-toggle-more-fieldsets]");
      if (cloned_nodes.length === 0) {
        toggle_element.style.display = "none";
      } else {
        toggle_element.style.display = "block";
      }
    }

    /**
     * This callback function is called when a bundle option changes or change event is triggered manually.
     * @param {*} dropdownElement
     * @param {*} rechageProductVariants
     * @param {*} shopifyProductVariants
     * @param {*} bundleVariants
     * @param {*} event
     */
    onBundleOptionChange(
      dropdownElement,
      rechageProductVariants,
      shopifyProductVariants,
      bundleVariants,
      event
    ) {
      const parentIndex = Number(event.target.getAttribute("data-index"));
      const parentOptionValue = event.target.value;
      const currentIndex = Number(dropdownElement.getAttribute("data-index"));
      const parentKey = `option${parentIndex + 1}`;
      const childKey = `option${currentIndex + 1}`;
      const parentNorm = String(parentOptionValue ?? "").trim();

      const variantRows = Array.isArray(rechageProductVariants) ? rechageProductVariants : [];

      // Filter variants based on the parent option value
      const matchingVariantRows = variantRows.filter((variant) => {
        return String(variant[parentKey] ?? "").trim() === parentNorm;
      });
      const availableOptionValues = matchingVariantRows
        .filter(isVariantAvailableForBundleSelection)
        .map((variant) => variant[childKey])
        .filter((value) => value !== undefined && value !== null)
        .map((value) => String(value).trim())
        .filter((value) => value.length > 0);

      const uniqueAvailableOptionValues = [...new Set(availableOptionValues)];

      // Disable all options initially
      Array.from(dropdownElement.options).forEach((option) => {
        option.disabled = true;
        option.classList.add("dropdown_variant--soldout");
      });

      // Enable options that are available
      uniqueAvailableOptionValues.forEach((value) => {
        const option = Array.from(dropdownElement.options).find(
          (o) => String(o.value).trim() === value
        );
        if (option) {
          option.disabled = false;
          option.classList.remove("dropdown_variant--soldout");
        }
      });

      // Keep the dropdown usable only when there is no matching variant data.
      if (uniqueAvailableOptionValues.length === 0 && matchingVariantRows.length === 0) {
        Array.from(dropdownElement.options).forEach((option) => {
          option.disabled = false;
          option.classList.remove("dropdown_variant--soldout");
        });
      }

      // Set the dropdown to the first available option if the current value is not available
      const currentSelected = String(dropdownElement.value ?? "").trim();
      if (!uniqueAvailableOptionValues.includes(currentSelected)) {
        const firstAvailableOptionIndex = Array.from(dropdownElement.options).findIndex(
          (option) => !option.disabled
        );
        dropdownElement.selectedIndex =
          firstAvailableOptionIndex >= 0 ? firstAvailableOptionIndex : 0;
      }

      this.computeBundleItemChanges(bundleVariants);
    }

    /**
     * Function to handle the change event for a bundle item.
     *
     * @param {Array} variants - The array of all variants of the bundle item.
     * @param {Event} event - The change event of the quantity input.
     * @returns {Promise} A promise that resolves when the function has completed.
     */
    async onBundleItemChange(variants, _event) {
      // Handle the change event of the select bundle item
      this.computeBundleItemChanges(variants);

      // Experiment GS-892
      document.dispatchEvent(new CustomEvent("Recharge:Bundle:QuantityUpdate"));
      // END Experiment GS-892
    }

    /**
     * Function to handle the change event of the quantity input for a bundle item.
     *
     * @param {HTMLElement} template - The template element of the bundle item.
     * @param {Object} selectedVariant - The selected variant of the bundle item.
     * @param {Array} variants - The array of all variants of the bundle item.
     * @param {Event} event - The change event of the quantity input.
     * @returns {Promise} A promise that resolves when the function has completed.
     */
    async onQuantityBundleItemChange(template, variants, event) {
      // Get the quantity input element
      let inputElement = event?.target?.querySelector("input.quantity__input");
      // If the quantity is greater than 0, check the checkbox
      if (Number(inputElement.value) > 0) {
        if (!template.querySelector('[type="checkbox"]').checked) {
          template.querySelector('[type="checkbox"]').checked = true;
        }
      } else {
        // If the quantity is not greater than 0, uncheck the checkbox
        if (template.querySelector('[type="checkbox"]').checked) {
          template.querySelector('[type="checkbox"]').checked = false;
        }
      }

      // Handle the change event of the select bundle item
      this.computeBundleItemChanges(variants);
      // Experiment GS-892
      document.dispatchEvent(new CustomEvent("Recharge:Bundle:QuantityUpdate"));
      // END Experiment GS-892
    }

    /**
     * Function to handle the change event of the select input for a bundle item.
     *
     * @param {HTMLElement} template - The template element of the bundle item.
     * @param {Array} variants - The array of all variants of the bundle item.
     * @param {Event} event - The change event of the select input.
     * @returns {Promise} A promise that resolves when the function has completed.
     */
    async onSelectBundleItemChange(template, variants, _event) {
      const checkbox = template.querySelector('[type="checkbox"]');
      const inputElement = template
        .querySelector("quantity-input.quantity")
        .querySelector("input.quantity__input");

      // Prevent checking checkbox if bundle limit is reached and quantity would be increased
      if (this.bundleMaxLimit !== null && checkbox && checkbox.checked) {
        const wrapper = this.querySelector(this.options.bundle_wrapper_selector);
        if (wrapper) {
          const currentTotal = Array.from(wrapper.querySelectorAll("input.quantity__input")).reduce(
            (sum, input) => {
              const bundleCardContent = input.closest(".bundle__card-content");
              return sum + (bundleCardContent ? parseInt(input.value) || 0 : 0);
            },
            0
          );

          // If checkbox is being checked and quantity is 0, it would set quantity to 1
          // Prevent this if it would exceed the limit
          if (Number(inputElement.value) === 0 && currentTotal >= this.bundleMaxLimit) {
            checkbox.checked = false;
            return;
          }
        }
      }

      // If the checkbox is checked and the quantity is 0, set the quantity to 1
      if (checkbox && checkbox.checked && Number(inputElement.value) === 0) {
        // Check if adding this item would exceed the limit
        if (this.bundleMaxLimit !== null) {
          const wrapper = this.querySelector(this.options.bundle_wrapper_selector);
          if (wrapper) {
            const currentTotal = Array.from(
              wrapper.querySelectorAll("input.quantity__input")
            ).reduce((sum, input) => {
              const bundleCardContent = input.closest(".bundle__card-content");
              return sum + (bundleCardContent ? parseInt(input.value) || 0 : 0);
            }, 0);

            if (currentTotal >= this.bundleMaxLimit) {
              checkbox.checked = false;
              return;
            }
          }
        }
        inputElement.value = 1;
      } else if (checkbox && !checkbox.checked && Number(inputElement.value) !== 0) {
        // If the checkbox is not checked and the quantity is not 0, set the quantity to 0
        inputElement.value = 0;
      }
      // Update the bundle item changes
      this.computeBundleItemChanges(variants);
      // Experiment GS-892
      document.dispatchEvent(new CustomEvent("Recharge:Bundle:QuantityUpdate"));
      // END Experiment GS-892
    }

    async computeBundleItemChanges(variants) {
      let wrapper = this.querySelector(this.options.bundle_wrapper_selector);
      // if the bundle is a bundle builder
      let isBundleBuilder = false;
      if (this.closest("product-info").querySelector("cstmbundle-builder")) {
        isBundleBuilder = true;
      }
      if (isBundleBuilder) {
        wrapper = this.closest("product-info").querySelector("cstmbundle-builder");
      }

      let pricesArray = [];
      let items = [];
      [...wrapper.querySelectorAll(".bundle__card")].forEach((card) => {
        let cardCheckbox = card.querySelector('.bundle__card-check input[type="checkbox"]');

        if (this.allow_checkbox_selector && !cardCheckbox.checked) {
          return;
        }

        [...card.querySelectorAll(".bundle__card-content")].forEach((item) => {
          let product = Object.assign({}, item.product);
          product.quantity = Number(item.querySelector(".quantity__input").value);
          product.selected_variant = product.data.variants.find((v) => v);
          if (product.data.variants.length > 1) {
            const options = [...item.querySelectorAll(".dropdown_variant")].reduce(
              (acc, el, idx) => {
                acc[`option${idx + 1}`] = el.value;
                return acc;
              },
              {}
            );

            product.selected_variant = product.data.variants.find((variant) =>
              Object.keys(options).every((key) => variant[key] === options[key])
            );
          }
          if (product.quantity) {
            items.push(product);
          }
        });
      });

      let allValidSelectedVariants = items.every((item) => item.selected_variant);
      if (allValidSelectedVariants) {
        document.dispatchEvent(new CustomEvent("Recharge:Widget:Valid"));
        //adds the variant title for showing on the cart drawer
        let selections = items.map((item) => {
          const unitPrice = normalizeShopifyVariantPrice(item.selected_variant);
          pricesArray.push(unitPrice * Number(item.quantity));
          return {
            variant_id: Number(item.selected_variant.id),
            product_id: Number(item.id),
            collection_id: Number(item.collection_id),
            quantity: Number(item.quantity),
            price: unitPrice,
            variant_title: item.selected_variant.title,
            title: item.data.title,
            selling_plan_groups: item?.data?.selling_plan_groups || [],
            subscription_price_exempt: bundleProductExemptFromSubscriptionPriceDisplay(item),
          };
        });
        let countSelected = selections.reduce((total, item) => {
          return total + item.quantity;
        }, 0);

        let closestVariant = this.findClosestVariantByCount(countSelected, variants);
        this.renderSubscriptionWidget(closestVariant);

        // Find the minimum items count among the variants
        let min = variants.map((v) => v.items_count).reduce((a, b) => Math.min(a, b));

        // Find the maximum items count among the variants
        let max = variants.map((v) => v.items_count).reduce((a, b) => Math.max(a, b));

        let range = closestVariant.ranges.find((r) => r);
        if (!closestVariant.items_count && range?.id) {
          min = range.quantity_min || 1;
          max = range.quantity_max || 99;
        }

        this.bundleMaxLimit = max;

        const toggleElements = (wrapper, disable) => {
          [...wrapper.querySelectorAll(".bundle__card")].forEach((item) => {
            item.querySelectorAll('button[name="plus"]').forEach((b) => (b.disabled = disable));
            item.querySelectorAll('input[type="number"]').forEach((b) => (b.disabled = disable));
            item
              .querySelectorAll('.bundle__card-check input[type="checkbox"]')
              .forEach((b) => (b.disabled = disable));
          });
        };
        if (countSelected < min) {
          toggleElements(wrapper, false);
        } else if (countSelected >= max) {
          toggleElements(wrapper, true);
        } else {
          toggleElements(wrapper, false);
        }

        const limitMessages = wrapper.querySelectorAll("[data-limit-message]");
        limitMessages.forEach((message) => {
          message.style.setProperty(
            "display",
            countSelected >= max ? "block" : "none",
            "important"
          );
        });

        if (countSelected !== closestVariant.items_count && closestVariant.items_count !== 0) {
          document.dispatchEvent(new CustomEvent("Recharge:Widget:BundleMaxDisable"));
        } else {
          if (countSelected > 0) {
            document.dispatchEvent(new CustomEvent("Recharge:Widget:BundleMaxEnable"));
          } else {
            document.dispatchEvent(new CustomEvent("Recharge:Widget:BundleMaxDisable"));
          }
        }

        document.dispatchEvent(
          new CustomEvent("Recharge:Widget:BundleItems", {
            detail: { items: selections },
          })
        );
      } else {
        // Disable button of one of the selected variant is invalid
        document.dispatchEvent(new CustomEvent("Recharge:Widget:BundleInvalid"));
      }
      if (wrapper) {
        syncBundleCardPriceAttributesInWrapper(wrapper);
      }
      document.dispatchEvent(
        new CustomEvent("bundle:item-change", {
          detail: { prices: pricesArray },
        })
      );
    }

    findClosestVariantByCount(count, variants) {
      return variants.reduce((closestObject, object) => {
        const difference = Math.abs(object.items_count - count);
        const minDifference = Math.abs(closestObject.items_count - count);

        return difference < minDifference ? object : closestObject;
      }, variants[0]);
    }

    renderSubscriptionWidget(variant) {
      let variantId = Number(variant.external_variant_id || variant.id);
      let selected = this.variants.find((v) => v.id === variantId);
      document.dispatchEvent(
        new CustomEvent("Recharge:Widget:Enable", {
          detail: {
            data: this.data,
            variant: selected,
            product_handle: this.product_handle,
          },
        })
      );
    }

    product(handle) {
      if (this.cache.hasOwnProperty(handle)) {
        return Promise.resolve(this.cache[handle]);
      }
      return new Promise((resolve) => {
        const resolveHandle = `${handle.includes("?view=") ? handle.split("?view=")[0] : handle}`;
        return Promise.all([
          fetch(`${handle}.json`, {
            headers: {
              "Content-Type": "application/json",
              Accept: "application/json",
            },
          }).then((response) => {
            return response.json();
          }),
          fetch(`${resolveHandle}?view=object`, {})
            .then((response) => {
              return response.text();
            })
            .then((response) => {
              let temp = document.createElement("div");
              temp.innerHTML = response;
              let section = temp.querySelector(".shopify-section");
              let json = JSON.parse(section.innerHTML);
              return json;
            }),
        ]).then((responses) => {
          let [productResponse, extra] = responses;
          const variantAvailabilityById = new Map(
            (extra.product?.variants || []).map((variant) => [
              String(variant.id),
              variant.available,
            ])
          );
          const variants = (productResponse.product.variants || []).map((variant) => {
            const availability = variantAvailabilityById.get(String(variant.id));
            return availability === undefined ? variant : { ...variant, available: availability };
          });
          let result = Object.assign(
            {},
            productResponse.product,
            { variants },
            { custom_metafields: extra.custom_metafields },
            { reharge_metafields: extra.reharge_metafields },
            { selling_plan_groups: extra.selling_plan_groups },
            { available: extra.available }
          );
          console.log("[RechargeProduct] Fetched product from backend", {
            handle: resolveHandle,
            product: result,
          });
          this.cache[handle] = result;
          return resolve(result);
        });
      });
    }

    async fetchAccessoryProducts() {
      try {
        const { collectionHandle, accessoriesTag } = getCstmBundleAddOnsConfig(this);

        let handles = [];

        if (collectionHandle) {
          handles = await fetchCollectionAccessoryHandles(collectionHandle, accessoriesTag);
        }

        if (!collectionHandle) {
          return [];
        }

        if (!handles.length) {
          const response = await fetch("/api/2025-04/graphql.json", {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              "X-Shopify-Storefront-Access-Token": "159136723c4a71560f30844a163a1e71",
            },
            body: JSON.stringify({
              query: `
              query AccessoryProducts {
                products(first: 250, query: "tag:${accessoriesTag}") {
                  edges {
                    node {
                      handle
                    }
                  }
                }
              }
            `,
            }),
          });

          const json = await response.json();
          const edges = json?.data?.products?.edges || [];
          handles = edges.map(({ node }) => node.handle).filter(Boolean);
        }

        if (!handles.length) return [];

        const products = await Promise.all(
          handles.map(async (handle) => {
            const productData = await this.product(`/products/${handle}`);
            return {
              id: productData.id,
              collection_id: 0,
              is_bundle_accessory: true,
              data: productData,
              variants: productData.variants || [],
              tags: Array.isArray(productData.tags)
                ? productData.tags
                : String(productData.tags || "")
                    .split(",")
                    .map((tag) => tag.trim())
                    .filter((tag) => tag.length),
            };
          })
        );

        return products.filter(
          (item) => Number(this.data.product.external_product_id) !== Number(item?.data?.id)
        );
      } catch (_err) {
        return [];
      }
    }

    /**
     * Fetches product and settings data from the Recharge CDN.
     * If the Recharge object is not available on the window, it retries after a delay.
     * @returns {Promise} A promise that resolves with the product and settings data.
     */
    fetch() {
      // Check if the Recharge object is available on the window
      if (window.recharge) {
        // Reset the CDN cache and fetch product and settings data
        return window.recharge.cdn
          .resetCDNCache()
          .then(() => window.recharge.cdn.getCDNProductAndSettings(this.id));
      }
      // Retry fetching after a delay if the Recharge object is not available
      return new Promise((resolve) => setTimeout(() => resolve(this.fetch()), 300));
    }

    get json() {
      if (this.hasAttribute("data-json")) {
        try {
          let json = JSON.parse(this.getAttribute("data-settings"));
          return json;
        } catch (_err) {}
      }
      return {};
    }
  }

  if (!window.customElements.get("recharge-product")) {
    window.customElements.define("recharge-product", RechargeProduct);
  }
})(window);
